import "./vote.css";

import { getInitials } from "@cohesive/ui-components";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useRef, useState } from "react";

import { tally, type Vote, type VoteValue } from "./voteState";

const SHOWN = 3;
const HUES = ["plum", "teal", "rust", "sage", "gold"] as const;

const SIDES = [
  { value: "up", label: "Thumbs up", Icon: ThumbsUp },
  { value: "down", label: "Thumbs down", Icon: ThumbsDown },
] as const;

function hueOf(uid: string) {
  let h = 0;
  for (const c of uid) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return HUES[h % HUES.length];
}

function Avatar({ vote }: { vote: Vote }) {
  const [failed, setFailed] = useState(false);
  return (
    <span
      className="vote-avatar"
      data-hue={hueOf(vote.uid)}
      title={vote.name || "Unnamed"}
    >
      {vote.avatarUrl && !failed ? (
        <img
          src={vote.avatarUrl}
          alt=""
          draggable={false}
          onError={() => setFailed(true)}
        />
      ) : (
        getInitials(vote.name) || "?"
      )}
    </span>
  );
}

function Voters({ votes }: { votes: Vote[] }) {
  const rest = votes.slice(SHOWN);
  return (
    <div className="vote-avatars">
      {votes.slice(0, SHOWN).map((v) => (
        <Avatar key={v.uid} vote={v} />
      ))}
      {rest.length > 0 && (
        <span
          className="vote-avatar vote-more"
          title={rest.map((v) => v.name || "Unnamed").join(", ")}
        >
          +{rest.length}
        </span>
      )}
    </div>
  );
}

const QUESTION_MAX = 80;
const PLACEHOLDER = "What are we voting on?";

/**
 * The optional question above the thumbs. Editors always get the row (a
 * prompt when empty), so their layout never shifts; everyone else sees it
 * only when a question is set.
 */
function Question({
  value,
  onChange,
}: {
  value: string;
  onChange?: (question: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // Enter and Escape settle the edit themselves; the blur that follows the
  // unmount must not commit a second time.
  const settled = useRef(false);

  if (!onChange) {
    return value ? (
      <p className="vote-question" title={value}>
        <span>{value}</span>
      </p>
    ) : null;
  }
  if (draft === null) {
    return (
      <button
        type="button"
        className="vote-question nodrag"
        data-empty={value ? undefined : true}
        title={value || undefined}
        onClick={() => {
          settled.current = false;
          setDraft(value);
        }}
      >
        <span>{value || PLACEHOLDER}</span>
      </button>
    );
  }
  const commit = () => {
    settled.current = true;
    onChange(draft.trim());
    setDraft(null);
  };
  return (
    <textarea
      className="vote-question vote-question-input nodrag"
      aria-label="Question"
      autoFocus
      rows={2}
      maxLength={QUESTION_MAX}
      placeholder={PLACEHOLDER}
      value={draft}
      onChange={(e) => setDraft(e.target.value.replace(/\n/g, " "))}
      onBlur={() => {
        if (!settled.current) commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          settled.current = true;
          setDraft(null);
        }
      }}
    />
  );
}

export interface VoteFaceProps {
  votes: Vote[];
  /** The viewer's own vote. */
  mine: VoteValue | null;
  /** What is being voted on; empty when none is set. */
  question?: string;
  /** Omitted for viewers, whose buttons are disabled. */
  onVote?: (value: VoteValue) => void;
  /** Editors only: sets the question, or clears it with "". */
  onQuestion?: (question: string) => void;
  /** Results only: the public viewer, where nobody can vote. */
  readOnly?: boolean;
}

export function VoteFace({
  votes,
  mine,
  question = "",
  onVote,
  onQuestion,
  readOnly,
}: VoteFaceProps) {
  const { up, down } = tally(votes);
  const total = up + down;
  const summary = total ? `${up} up · ${down} down` : "No votes yet";
  return (
    <div className="vote">
      <Question value={question} onChange={readOnly ? undefined : onQuestion} />
      <div className="vote-sides">
        {SIDES.map(({ value, label, Icon }) => {
          const count = value === "up" ? up : down;
          const face = (
            <>
              <Icon aria-hidden="true" />
              <span className="vote-n">{count}</span>
            </>
          );
          const name = `${label}: ${count}`;
          const button = readOnly ? (
            <span
              className="vote-btn"
              data-side={value}
              role="img"
              aria-label={name}
            >
              {face}
            </span>
          ) : (
            <button
              type="button"
              className="vote-btn nodrag"
              data-side={value}
              aria-label={name}
              aria-pressed={mine === value}
              disabled={!onVote}
              onClick={() => onVote?.(value)}
            >
              {face}
            </button>
          );
          return (
            <div className="vote-side" key={value}>
              {readOnly || onVote ? (
                button
              ) : (
                // A disabled button fires no pointer events, so the tooltip
                // sits on a wrapper.
                <span title="Only editors can vote">{button}</span>
              )}
              <Voters votes={votes.filter((v) => v.value === value)} />
            </div>
          );
        })}
      </div>
      <div className="vote-bar" role="img" aria-label={summary}>
        {total > 0 && (
          <>
            <i
              className="vote-bar-up"
              style={{ width: `${(up / total) * 100}%` }}
            />
            <i
              className="vote-bar-down"
              style={{ width: `${(down / total) * 100}%` }}
            />
          </>
        )}
      </div>
      <div className="vote-caption" aria-hidden="true">
        {summary}
      </div>
    </div>
  );
}

const PREVIEW_VOTES: Vote[] = [
  { uid: "preview-1", name: "Ada Lovelace", at: 1, value: "up" },
  { uid: "preview-2", name: "Grace Hopper", at: 2, value: "up" },
  { uid: "preview-3", name: "Alan Turing", at: 3, value: "up" },
  { uid: "preview-4", name: "Tim Berners-Lee", at: 4, value: "down" },
];

export function VotePreview() {
  return (
    <VoteFace votes={PREVIEW_VOTES} mine={null} question="Ship it on Friday?" />
  );
}
